/**
 * SMS Credit Calculation Utility
 * Calculates SMS credits based on message content and character encoding
 */

export type CreditData = {
	messageLeft: number
	totalMessage: number
	credit: number
	lang: 'eng' | 'unicode'
}

/**
 * GSM 7-bit special characters that count as 2 characters
 * Reference: GSM 03.38 standard
 */
export const CHARACTERS = [
	{ character: '^', count: 2 },
	{ character: '{', count: 2 },
	{ character: '}', count: 2 },
	{ character: '\\', count: 2 },
	{ character: '[', count: 2 },
	{ character: ']', count: 2 },
	{ character: '~', count: 2 },
	{ character: '|', count: 2 },
	{ character: '€', count: 2 },
] as const

/**
 * GSM 7-bit basic character set
 * Characters in this set count as 1 character in English mode
 */
const GSM_7BIT_BASIC =
	'@£$¥èéùìòÇ\\nØø\\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\\"#¤%&\'()*+,-./0123456789:;<=>?' +
	'ABCDEFGHIJKLMNOPQRSTUVWXYZ' +
	'abcdefghijklmnopqrstuvwxyz' +
	'ÄÖÑÜäöñüà'

/**
 * GSM 7-bit extended character set (counts as 2 characters)
 */
const GSM_7BIT_EXTENDED = '^{}\\[]~|€'

/**
 * Check if a message uses only GSM 7-bit characters (English mode)
 * @param message - The message to check
 * @returns true if message uses only GSM 7-bit characters
 */
export const isEnglishAndNotInCharacterSet = (message: string): boolean => {
	const gsmCharSet = GSM_7BIT_BASIC + GSM_7BIT_EXTENDED

	for (const char of message) {
		if (!gsmCharSet.includes(char)) {
			return false
		}
	}

	return true
}

/**
 * Calculate SMS credits and character counts for a message
 * @param message - The message text to calculate credits for
 * @returns Credit data including remaining characters, total available, credit count, and language mode
 *
 * @example
 * const creditData = calculateCredit("Hello world")
 * // Returns: { messageLeft: 149, totalMessage: 160, credit: 1, lang: 'eng' }
 *
 * @example
 * const creditData = calculateCredit("สวัสดี")
 * // Returns: { messageLeft: 64, totalMessage: 70, credit: 1, lang: 'unicode' }
 */
export const calculateCredit = (message?: string): CreditData => {
	// Handle empty or undefined message
	if (!message || message.trim() === '') {
		return { messageLeft: 0, totalMessage: 0, credit: 0, lang: 'eng' }
	}

	let lang: 'eng' | 'unicode'

	// Process the message to replace [link][destination][redirector] patterns
	// Only count the redirector URL (third bracket) plus "/XXXXXX"
	const processedMessage = message.replace(
		/\[link\]\[(.*?)\]\[(.*?)\]/gi,
		(_1, _2, redirector) => {
			return `${redirector}/XXXXXX`
		},
	)

	if (isEnglishAndNotInCharacterSet(processedMessage)) {
		lang = 'eng'
	} else {
		lang = 'unicode'
	}

	const maxCredit = 8
	let totalMessageLength = 0

	const characterCounts: Record<string, number> = {}

	CHARACTERS.forEach(({ character, count }) => {
		characterCounts[character] = count
	})

	// Calculate total message length based on character counts
	if (lang === 'unicode') {
		totalMessageLength = processedMessage.length
	} else {
		for (const char of processedMessage) {
			totalMessageLength += characterCounts[char] || 1 // If count is not specified, default to 1
		}
	}

	// Determine credit based on message length and language
	let messageLeft: number
	let totalMessage: number
	let credit: number

	if (lang === 'eng') {
		if (totalMessageLength <= 160) {
			messageLeft = 160 - totalMessageLength
			totalMessage = 160
			credit = 1
		} else {
			credit = Math.ceil(totalMessageLength / 153)
			if (credit > maxCredit) {
				throw new Error('Credit exceeds the maximum limit of 8.')
			}
			messageLeft = credit * 153 - totalMessageLength
			totalMessage = credit * 153
		}
	} else if (lang === 'unicode') {
		if (totalMessageLength <= 70) {
			messageLeft = 70 - totalMessageLength
			totalMessage = 70
			credit = 1
		} else {
			credit = Math.ceil(totalMessageLength / 67)
			if (credit > maxCredit) {
				throw new Error('Credit exceeds the maximum limit of 8.')
			}
			messageLeft = credit * 67 - totalMessageLength
			totalMessage = credit * 67
		}
	} else {
		throw new Error(
			"Unsupported language. Only 'eng' and 'unicode' are supported.",
		)
	}

	return { messageLeft, totalMessage, credit, lang }
}

/**
 * Get a human-readable description of the language mode
 */
export const getLanguageLabel = (lang: 'eng' | 'unicode'): string => {
	return lang === 'eng' ? 'Eng' : 'Unicode'
}
